<template>
  <div class="base-table-container">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="row-selector">
        <label class="d-flex align-items-center mb-0">
          Show 
          <select 
            :value="pageSize" 
            class="form-select form-select-sm mx-2 w-auto" 
            @change="$emit('update:pageSize', Number($event.target.value))"
          >
            <option v-for="size in [10, 20, 50, 100]" :key="size" :value="size">{{ size }}</option>
          </select>
          entries
        </label>
      </div>
      <div class="table-tools d-flex align-items-center gap-2">
        <slot name="tools"></slot>

        <div class="column-toggle position-relative" ref="columnMenuRef">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary d-flex align-items-center"
            @click="showColumnMenu = !showColumnMenu"
          >
            <i class="bi bi-layout-three-columns me-1"></i> Columns
          </button>

          <div v-if="showColumnMenu" class="column-menu shadow-sm border rounded bg-white">
            <div class="d-flex justify-content-between align-items-center px-2 py-1 border-bottom">
              <small class="fw-bold text-muted">Show columns</small>
              <button type="button" class="btn btn-link btn-sm p-0 text-decoration-none" @click="resetColumns">Reset</button>
            </div>
            <div class="column-menu-list p-1">
              <label
                v-for="col in hideableColumns"
                :key="col.key"
                class="d-flex align-items-center px-2 py-1 mb-0 column-menu-item"
              >
                <input
                  type="checkbox"
                  class="form-check-input me-2 mt-0"
                  :checked="!hiddenKeys.has(col.key)"
                  @change="toggleColumn(col.key)"
                >
                <span class="small">{{ col.label }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="table-responsive border rounded" :style="{ maxHeight: maxHeight }">
      <table class="table table-hover table-striped mb-0 text-nowrap">
        <thead class="table-light sticky-top">
          <tr>
            <th v-if="showCheckbox" class="text-center" style="width: 40px">
              <input type="checkbox" :checked="isAllSelected" @change="toggleSelectAll">
            </th>
            
            <th
              v-for="col in visibleColumns"
              :key="col.key"
              :style="{ width: col.width || 'auto', textAlign: col.align || 'left' }"
              :class="{ 'cursor-pointer': col.sortable }"
              @click="col.sortable && handleSort(col.key)"
            >
              {{ col.label }}
              <span v-if="col.sortable" class="ms-1 small text-muted">
                {{ sortKey === col.key ? (sortOrder === 'asc' ? '↑' : '↓') : '↕' }}
              </span>
            </th>
            
            <th v-if="$slots.actions" class="text-center">Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="loading">
            <td :colspan="totalColumns" class="text-center py-5">
              <div class="spinner-border spinner-border-sm text-primary me-2"></div>
              <span>Loading...</span>
            </td>
          </tr>

          <tr v-else-if="!data.length">
            <td :colspan="totalColumns" class="text-center py-5 text-muted">
              No data available
            </td>
          </tr>

          <tr v-else v-for="(item, index) in data" :key="item.id || index">
            <td v-if="showCheckbox" class="text-center">
              <input type="checkbox" :checked="isSelected(item)" @change="toggleSelect(item)">
            </td>
            
            <td v-for="col in visibleColumns" :key="col.key" :style="{ textAlign: col.align || 'left' }">
              <slot :name="`cell(${col.key})`" :item="item" :index="index">
                {{ col.formatter ? col.formatter(item[col.key], item) : item[col.key] }}
              </slot>
            </td>

            <td v-if="$slots.actions" class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <slot name="actions" :item="item"></slot>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3">
      <div class="text-muted small">
        Showing {{ fromRecord }} to {{ toRecord }} of {{ totalItems }} entries
      </div>
      
      <nav v-if="totalPages > 1">
        <ul class="pagination pagination-sm mb-0">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="$emit('update:currentPage', currentPage - 1)"><span aria-hidden="true">«</span></button>
          </li>
          
          <li v-for="p in visiblePages" :key="p" class="page-item" :class="{ active: p === currentPage, disabled: p === '...' }">
            <button class="page-link" @click="p !== '...' && $emit('update:currentPage', p)">{{ p }}</button>
          </li>

          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="$emit('update:currentPage', currentPage + 1)"><span aria-hidden="true">»</span></button>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  columns: { type: Array, required: true },
  data: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  totalItems: { type: Number, default: 0 },
  currentPage: { type: Number, default: 1 },
  pageSize: { type: Number, default: 10 },
  maxHeight: { type: String, default: 'auto' },
  showCheckbox: { type: Boolean, default: false },
  // Khi truyền vào, lựa chọn ẩn/hiện cột sẽ được lưu theo key này trong localStorage
  storageKey: { type: String, default: '' }
});

const emit = defineEmits(['update:currentPage', 'update:pageSize', 'sort', 'selection-change']);

// ── Column visibility ──────────────────────────────────────────────
// Cột có thể ẩn/hiện (trừ cột đặt hideable: false thì luôn hiển thị).
const hiddenKeys = ref(new Set());
const showColumnMenu = ref(false);
const columnMenuRef = ref(null);

const hideableColumns = computed(() => props.columns.filter(c => c.hideable !== false));
const visibleColumns = computed(() =>
  props.columns.filter(c => c.hideable === false || !hiddenKeys.value.has(c.key))
);

const storageId = computed(() => props.storageKey ? `baseTable:hiddenCols:${props.storageKey}` : '');

const toggleColumn = (key) => {
  if (hiddenKeys.value.has(key)) hiddenKeys.value.delete(key);
  else hiddenKeys.value.add(key);
};

const resetColumns = () => hiddenKeys.value.clear();

// Lưu / khôi phục lựa chọn theo storageKey
onMounted(() => {
  if (storageId.value) {
    try {
      const saved = JSON.parse(localStorage.getItem(storageId.value) || '[]');
      if (Array.isArray(saved)) hiddenKeys.value = new Set(saved);
    } catch (_) { /* bỏ qua dữ liệu hỏng */ }
  }
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside));

watch(hiddenKeys, (val) => {
  if (storageId.value) localStorage.setItem(storageId.value, JSON.stringify(Array.from(val)));
}, { deep: true });

const handleClickOutside = (e) => {
  if (showColumnMenu.value && columnMenuRef.value && !columnMenuRef.value.contains(e.target)) {
    showColumnMenu.value = false;
  }
};

// Sorting
const sortKey = ref('');
const sortOrder = ref('asc');

const handleSort = (key) => {
  sortOrder.value = sortKey.value === key && sortOrder.value === 'asc' ? 'desc' : 'asc';
  sortKey.value = key;
  emit('sort', { key: sortKey.value, order: sortOrder.value });
};

// Selection
const selectedIds = ref(new Set());
const toggleSelect = (item) => {
  if (selectedIds.value.has(item.id)) selectedIds.value.delete(item.id);
  else selectedIds.value.add(item.id);
  emit('selection-change', Array.from(selectedIds.value));
};

const isSelected = (item) => selectedIds.value.has(item.id);
const isAllSelected = computed(() => props.data.length > 0 && selectedIds.value.size === props.data.length);

const toggleSelectAll = () => {
  if (isAllSelected.value) selectedIds.value.clear();
  else props.data.forEach(item => selectedIds.value.add(item.id));
  emit('selection-change', Array.from(selectedIds.value));
};

// Pagination Logic
const totalPages = computed(() => Math.ceil(props.totalItems / props.pageSize) || 1);
const fromRecord = computed(() => props.data.length === 0 ? 0 : (props.currentPage - 1) * props.pageSize + 1);
const toRecord = computed(() => Math.min(props.currentPage * props.pageSize, props.totalItems));

const visiblePages = computed(() => {
  const pages = [];
  const total = totalPages.value;
  const current = props.currentPage;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3) pages.push('...');
    
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    
    for (let i = start; i <= end; i++) pages.push(i);
    
    if (current < total - 2) pages.push('...');
    pages.push(total);
  }
  return pages;
});

const totalColumns = computed(() => {
  return visibleColumns.value.length + (props.showCheckbox ? 1 : 0) + 1; // +1 cho actions
});
const clearSelection = () => {
  selectedIds.value.clear();
  emit('selection-change', []);
};

defineExpose({ clearSelection });
</script>

<style scoped>
.cursor-pointer { cursor: pointer; }

/* Dropdown chọn cột hiển thị */
.column-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 1050;
  min-width: 200px;
  max-height: 320px;
  overflow-y: auto;
  background: #fff;
}
.column-menu-item {
  cursor: pointer;
  border-radius: 4px;
}
.column-menu-item:hover {
  background: #f5f5f5;
}
.column-menu-item .form-check-input {
  cursor: pointer;
}

/* Giữ header đứng yên khi cuộn dọc */
.sticky-top {
  z-index: 10;
  background: white;
}

/* Ép table không được co quá mức gây rớt dòng */
.table {
  min-width: 800px; /* Bạn có thể tăng số này lên nếu bảng có nhiều cột hơn */
}

/* Đảm bảo các ô trong table không tự ý xuống dòng */
.table th, .table td {
  white-space: nowrap;
  vertical-align: middle;
}

/* Nếu bạn muốn một số cột cụ thể (như tên khách hàng dài) được xuống dòng
   thì có thể tạo class riêng, còn mặc định sẽ nằm trên 1 dòng */
.text-wrap-custom {
  white-space: normal !important;
  min-width: 200px;
}

/* Tùy chỉnh thanh cuộn cho đẹp hơn (tùy chọn) */
.table-responsive::-webkit-scrollbar {
  height: 6px;
}
.table-responsive::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 10px;
}
</style>